import React, { useCallback, useEffect, useRef } from "react";
import {
  Award,
  Building,
  Calendar,
  ChevronDown,
  ChevronUp,
  Eye,
  User,
  Users,
} from "lucide-react";
import { getUserInitials } from "../utils/avatarUtils";

// Employees table with hierarchy expand/collapse and avatar rendering
const EmployeeTable = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  fonts,
  spacing,
  filteredEmployees,
  employeeProfilePics,
  requestPic,
  toggleEmployeeExpand,
  onViewEmployee,
}) => {
  // Only ask for an avatar once its row is actually on screen.
  //
  // This table renders every row of the hierarchy unpaginated, so a manager
  // with 1,000 reports produces 1,000 rows. Eagerly fetching an avatar per row
  // saturated the browser's ~6-connections-per-origin budget and starved the
  // whole app of network — navigation and the sidebar would hang while a
  // thousand avatars downloaded. With this observer only the ~15 visible rows
  // ever request, and the hook caps concurrency at 2.
  //
  // The observer is created LAZILY, on first use, NOT in a useEffect.
  // Ref callbacks run during commit, BEFORE effects — so an observer built in
  // useEffect is still null when the first rows mount, and those rows are
  // never observed. That is a silent failure: no error, just no pictures.
  const observerRef = useRef(null);
  const requestPicRef = useRef(requestPic);
  requestPicRef.current = requestPic;

  const getObserver = () => {
    if (observerRef.current) return observerRef.current;
    if (typeof IntersectionObserver === "undefined") return null;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute("data-emp-id");
          if (id && typeof requestPicRef.current === "function") {
            requestPicRef.current(id);
          }
          observerRef.current?.unobserve(entry.target); // one request per row
        });
      },
      { rootMargin: "200px" }, // start slightly before the row scrolls in
    );
    return observerRef.current;
  };

  const observeRow = useCallback((node) => {
    if (!node) return;
    const io = getObserver();
    if (io) {
      io.observe(node);
    } else {
      // No IntersectionObserver (very old browser / jsdom): fall back to
      // requesting immediately so avatars still appear.
      const id = node.getAttribute("data-emp-id");
      if (id && typeof requestPicRef.current === "function") {
        requestPicRef.current(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  return (
  <div style={styles.tableContainer}>
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <User size={14} />
                {text[language].employee}
              </div>
            </th>
            {screenSize !== "xs" && screenSize !== "sm" && (
              <th style={styles.tableHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Building size={14} />
                  {text[language].department}
                </div>
              </th>
            )}
            {screenSize !== "xs" && (
              <th style={styles.tableHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Calendar size={14} />
                  {text[language].joinDate}
                </div>
              </th>
            )}
            <th style={{ ...styles.tableHeader, textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                <Award size={14} />
                {text[language].actions}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredEmployees.map((emp) => (
            <tr
              key={emp.id}
              ref={observeRow}
              data-emp-id={emp.id}
              style={styles.tableRow}
              onClick={() => onViewEmployee(emp)}
            >
              {/* Employee Info with hierarchy indentation and expand/collapse */}
              <td style={styles.tableCell}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.gap,
                    paddingLeft: `${emp.level * (screenSize === "xs" ? 1 : 2)}rem`,
                  }}
                >
                  {/* Expand/Collapse button for employees with children */}
                  {(emp.level === 0 || emp.hasChildren) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleEmployeeExpand(emp.id);
                      }}
                      style={{
                        background:
                          theme === "dark"
                            ? "rgba(59, 130, 246, 0.1)"
                            : "rgba(59, 130, 246, 0.08)",
                        border: `1px solid ${theme === "dark"
                          ? "rgba(59, 130, 246, 0.3)"
                          : "rgba(59, 130, 246, 0.2)"
                          }`,
                        borderRadius: "0.375rem",
                        cursor: "pointer",
                        padding: "0.375rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#2e7cba",
                        transition: "all 0.2s ease",
                        width: "1.75rem",
                        height: "1.75rem",
                      }}
                      title={emp.isExpanded ? "Collapse" : "Expand"}
                    >
                      {emp.isExpanded ? (
                        <ChevronDown size={14} strokeWidth={2.5} />
                      ) : (
                        <ChevronUp size={14} strokeWidth={2.5} style={{ transform: "rotate(-90deg)" }} />
                      )}
                    </button>
                  )}

                  {/* Show manager badge if has children */}
                  {emp.hasChildren && (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
                        borderRadius: "0.375rem",
                        padding: "0.25rem 0.5rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                      }}
                    >
                      <Users size={12} color="white" />
                      <span
                        style={{
                          fontSize: fonts.tiny,
                          color: "white",
                          fontWeight: "600",
                        }}
                      >
                        Manager
                      </span>
                    </div>
                  )}

                  {/* Profile Picture (show image if available, otherwise initials) */}
                  {employeeProfilePics[emp.id] ? (
                    <img
                      src={employeeProfilePics[emp.id]}
                      alt={emp?.name || "Employee"}
                      style={{
                        ...styles.userAvatar,
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}

                  <div
                    style={{
                      ...styles.userAvatar,
                      display: employeeProfilePics[emp.id] ? "none" : "flex",
                    }}
                  >
                    {getUserInitials(emp?.name)}
                  </div>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: fonts.body,
                        color: emp.hasChildren ? "#2e7cba" : "inherit",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      {/* Status Circle Indicator */}
                      <div
                        style={{
                          width: screenSize === "xs" ? "0.625rem" : "0.75rem",
                          height: screenSize === "xs" ? "0.625rem" : "0.75rem",
                          borderRadius: "50%",
                          backgroundColor:
                            emp.status === "Active"
                              ? "#10b981"
                              : emp.status === "OnLeave"
                                ? "#f59e0b"
                                : "#ef4444",
                          boxShadow: `0 0 0 2px ${theme === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.05)"
                            }, 0 0 8px ${emp.status === "Active"
                              ? "rgba(16, 185, 129, 0.4)"
                              : emp.status === "OnLeave"
                                ? "rgba(245, 158, 11, 0.4)"
                                : "rgba(239, 68, 68, 0.4)"
                            }`,
                          animation: "pulse 2s infinite",
                          flexShrink: 0,
                        }}
                        title={
                          emp.status === "Active"
                            ? text[language].statusActive
                            : emp.status === "OnLeave"
                              ? text[language].statusOnLeave
                              : text[language].statusInactive
                        }
                      />
                      {emp.name.split(" ").slice(0, 2).join(" ")}
                    </div>
                    {emp.department && (
                      <div
                        style={{
                          fontSize: fonts.tiny,
                          opacity: "0.6",
                          marginTop: "0.125rem",
                          color: "#2e7cba",
                        }}
                      >
                        {emp.department}
                      </div>
                    )}
                  </div>
                </div>
              </td>

              {/* Department */}
              {screenSize !== "xs" && screenSize !== "sm" && (
                <td style={styles.tableCell}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Building size={14} style={{ opacity: "0.6" }} />
                    {emp.department}
                  </div>
                </td>
              )}

              {/* Join Date */}
              {screenSize !== "xs" && (
                <td style={styles.tableCell}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Calendar size={14} style={{ opacity: "0.6" }} />
                    {emp.joinDate
                      ? new Date(emp.joinDate).toLocaleDateString("en-US", {
                        year: screenSize === "sm" ? "2-digit" : "numeric",
                        month: "short",
                        day: "numeric",
                      })
                      : "N/A"}
                  </div>
                </td>
              )}

              {/* Actions */}
              <td style={{ ...styles.tableCell, textAlign: "center" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    style={{ ...styles.actionBtn, ...styles.viewBtn }}
                    title={text[language].viewDetails}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewEmployee(emp);
                    }}
                  >
                    <Eye size={screenSize === "xs" ? 12 : 14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

// React.memo is load-bearing here, not a micro-optimisation.
//
// This table renders EVERY row of the manager's hierarchy with no pagination,
// so a manager with ~1,000 reports means ~1,000 rows in the DOM. The sidebar
// lives above this page in the tree and re-renders it on collapse/expand and
// on every nav-item hover. Without this memo, each of those interactions
// reconciled all 1,000 rows, which is why the sidebar opened slowly.
//
// For this to actually work the props must be referentially stable —
// `filteredEmployees` and `styles` are memoized in EmployeeManagementManager
// for exactly that reason. If you add a prop here, make sure it is not a
// freshly-built object/array/function on every parent render, or this memo
// silently stops helping.
export default React.memo(EmployeeTable);
